import { zodResolver } from "@hookform/resolvers/zod";
import {
  formatProvider,
  V2Alert,
  V2AlertTitle,
  V2Button,
  V2Checkbox,
  V2ComboBoxItem,
  V2Heading,
  V2SingleSelectComboBox,
  V2TextArea,
  V2TextField,
  V2NumberField,
  V2Divider,
} from "@stacklet/ui";
import pluralize from "pluralize";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  ConnectionHandler,
  graphql,
  useFragment,
  useMutation,
} from "react-relay";

import { ErrorAlerts } from "app/components/ErrorAlerts";
import { ACLFormFields, type Role } from "app/components/rbac/ACLFormFields";
import {
  EDIT_FORM_LOADING_SUBMIT_BUTTON_NAME,
  EDIT_FORM_SUBMIT_BUTTON_NAME,
} from "app/constants";
import { useProvider } from "app/contexts/ProviderContext";
import { useFormErrors } from "app/hooks";
import {
  setRoleAssignmentTarget,
  useApplyRoleChanges,
} from "app/hooks/rbac/useApplyRoleChanges";
import { EditPolicyCollectionMutation } from "app/mutations";
import { useAddBindings } from "app/mutations/hooks/useAddBindings";
import { useAddPolicyCollection } from "app/mutations/hooks/useAddPolicyCollection";
import { getUserPermissions } from "app/utils/rbac/permissions";
import { AccountGroupPicker } from "app/views/admin/bindings/components/AccountGroupPicker";
import { formatRateExpression } from "app/views/admin/bindings/utils";

import { type UpsertDpcForm_policyCollection$key } from "./__generated__/UpsertDpcForm_policyCollection.graphql";
import { type UpsertDpcForm_query$key } from "./__generated__/UpsertDpcForm_query.graphql";
import { schema, type PolicyCollection } from "./schema";

import type { AddPolicyCollectionMutation$data } from "app/mutations/__generated__/AddPolicyCollectionMutation.graphql";
import type {
  EditPolicyCollectionMutation$data,
  EditPolicyCollectionMutation as EditPolicyCollectionMutationType,
} from "app/mutations/__generated__/EditPolicyCollectionMutation.graphql";
import type { PayloadError } from "relay-runtime";

const POLICY_COLLECTION_ROLES: Role[] = ["owner", "editor"];

interface Props {
  onCancel: () => void;
  onAdded: (message: string, uuid: string) => void;
  repositoryUUID: string;
  connectionId?: string;
  canDeployPolicies?: boolean;
  queryRef?: UpsertDpcForm_policyCollection$key;
  queryRefAcl?: UpsertDpcForm_query$key;
}

export function UpsertDpcForm({
  onCancel,
  onAdded,
  connectionId,
  repositoryUUID,
  canDeployPolicies = false,
  queryRef,
  queryRefAcl,
}: Props) {
  const data = useFragment<UpsertDpcForm_policyCollection$key>(
    graphql`
      fragment UpsertDpcForm_policyCollection on PolicyCollection {
        name
        description
        autoUpdate
        uuid
        repositoryView {
          branchName
          policyDirectories
          policyFileSuffix
        }
        userPermissions
        ...ACLFormFields_acl
      }
    `,
    queryRef,
  );

  const dataQuery = useFragment(
    graphql`
      fragment UpsertDpcForm_query on Query
      @argumentDefinitions(first: { type: Int }) {
        ...ACLFormFields_query
        ...AccountGroupPicker_accountGroups @arguments(first: $first)
      }
    `,
    queryRefAcl,
  );

  const isEditMode = !!data?.name;

  const provider = useProvider();

  const policyCollectionBindingsConnection = ConnectionHandler.getConnectionID(
    "client:root",
    "BindingsTable_bindings",
  );

  const bindingsConnection = ConnectionHandler.getConnectionID(
    "client:root",
    "Bindings_bindings",
    {
      filterElement: {
        single: { name: "provider", value: formatProvider(provider) },
      },
    },
  );

  const addBindingConnections = [
    policyCollectionBindingsConnection,
    bindingsConnection,
  ];

  const { canViewAcl, canEditAcl } = getUserPermissions(data?.userPermissions);

  const [addPolicyCollection, isInFlightAdd] = useAddPolicyCollection();
  const [commit, isInFlightEdit] =
    useMutation<EditPolicyCollectionMutationType>(EditPolicyCollectionMutation);

  const [addBindings, isInFlightAddBindings] = useAddBindings();

  const [formErrors, setFormErrors] = useFormErrors();

  const methods = useForm<PolicyCollection>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      name: data?.name || "",
      description: data?.description || "",
      provider,
      repositoryUUID,
      autoUpdate: true,
      accountGroups: [],
      repositoryView: {
        branchName: data?.repositoryView?.branchName || "",
        policyDirectories: [...(data?.repositoryView?.policyDirectories || [])],
        policyFileSuffix: [...(data?.repositoryView?.policyFileSuffix || [])],
      },
      period: 7,
      unit: "day" as const,
      executeAssetDb: "enabled" as const,
      policyActions: "disabled" as const,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    watch,
  } = methods;

  const {
    roleChanges,
    handleRoleChanges,
    applyRoleChanges,
    isInFlightRoleAssignment,
  } = useApplyRoleChanges(setFormErrors, (store) => {
    // Invalidate the policy collection record to refresh it
    if (data?.uuid) {
      // Get the record ID for the policy collection
      const recordId = `PolicyCollection:${data.uuid}`;
      // Get the record
      const record = store.get(recordId);

      // If record found, invalidate it
      if (record) {
        record.invalidateRecord();
      } else {
        // Try to find the record through the root
        const root = store.getRoot();
        const collectionField = root.getLinkedRecord("policyCollection", {
          uuid: data.uuid,
        });

        if (collectionField) {
          collectionField.invalidateRecord();
        }
      }
    }
  });

  function add(formData: PolicyCollection) {
    const { name, description, autoUpdate, repositoryView, accountGroups } =
      formData;

    addPolicyCollection(
      {
        input: {
          name,
          description,
          provider: formatProvider(provider),
          autoUpdate,
          repositoryUUID,
          repositoryView,
        },
        connections: [connectionId || ""],
      },
      handleCompleted,
    );

    function handleCompleted(
      response: AddPolicyCollectionMutation$data,
      errors: PayloadError[] | null,
    ) {
      if (errors?.length) {
        setFormErrors(errors.map((error) => error.message));
        return;
      }

      const policyCollection = response.addPolicyCollection?.collection;
      if (policyCollection) {
        const handleApplyRoleChanges = () => {
          // In create/add mode we have no role assignment target until this step
          // We need to set the role assignment target before applying role changes
          const roleAssignments = setRoleAssignmentTarget(
            roleChanges,
            policyCollection.roleAssignmentTarget,
          );

          applyRoleChanges(() => {
            onAdded(
              `${policyCollection.name} added successfully`,
              policyCollection.uuid,
            );
          }, roleAssignments);
        };

        if (accountGroups?.length) {
          // Build addBinding input for each account group
          const addBindingInputs =
            accountGroups.map((group) =>
              buildAddBindingInput(policyCollection.uuid, group, formData),
            ) || [];
          // Show success message after all bindings have been added
          addBindings({
            inputs: addBindingInputs,
            onAllCompleted: () => {
              // All bindings added successfully, now apply role changes
              handleApplyRoleChanges();
            },
            onAnyError: (errors) => {
              if (errors?.length) {
                // Add account group uuid to binding error to help user debug problem source
                setFormErrors(
                  errors?.map(
                    (error) =>
                      `Binding for Account Group: ${error.accountGroupUUID}: ${error.message}`,
                  ),
                );
              }
            },
          });
        } else {
          // No account groups selected, just handle applying role changes
          handleApplyRoleChanges();
        }
      }
    }
  }

  function buildAddBindingInput(
    policyCollectionUUID: string,
    accountGroup: { value: string; label: string },
    formData: PolicyCollection,
  ) {
    const {
      name: collectionName,
      period,
      unit,
      executeAssetDb,
      policyActions,
    } = formData;

    const schedule = period && unit ? formatRateExpression(unit, period) : "";

    const dryRun = policyActions === "disabled";

    const executeSourceVar =
      executeAssetDb === "enabled"
        ? {
            stacklet: {
              source: {
                default: "assetdb",
              },
            },
          }
        : "";

    const name = `${collectionName}: ${accountGroup.label}`;
    const description = `Binding generated for dynamic policy collection ${collectionName} on account group ${accountGroup.label}`;

    return {
      input: {
        name,
        description,
        accountGroupUUID: accountGroup.value,
        policyCollectionUUID,
        deploy: true,
        autoDeploy: true,
        ...(schedule ? { schedule } : { schedule: "" }),
        executionConfig: {
          dryRun: {
            default: dryRun,
          },
          variables: executeSourceVar ? JSON.stringify(executeSourceVar) : "",
        },
      },
      connections: addBindingConnections,
    };
  }

  function update(formData: PolicyCollection) {
    const { name, description, repositoryView } = formData;

    commit({
      variables: {
        input: {
          name,
          description,
          repositoryView,
          uuid: data?.uuid || "",
        },
        connections: [connectionId || ""],
        removeMappingsInput: {
          ids: [],
        },
        updateMappingsInput: {
          mappings: [],
        },
      },
      onCompleted: (response: EditPolicyCollectionMutation$data, errors) => {
        if (errors?.length) {
          setFormErrors(errors.map((error) => error.message));
          return;
        }

        const policyCollection = response.updatePolicyCollection?.collection;
        if (policyCollection) {
          // Apply role changes if there are any
          applyRoleChanges(() => {
            onAdded(
              `${policyCollection.name} updated successfully`,
              policyCollection.uuid,
            );
          });
        }
      },
    });
  }

  const period = watch("period");
  const accountGroups = watch("accountGroups");
  const hasAccountGroups = Boolean(accountGroups?.length);

  const isInFlight =
    isInFlightAdd ||
    isInFlightEdit ||
    isInFlightRoleAssignment ||
    isInFlightAddBindings;

  const showAccessControl = isEditMode ? canViewAcl : true;

  const showDeployAndRun = !isEditMode && dataQuery && canDeployPolicies;

  return (
    <FormProvider {...methods}>
      <form
        className="mt-xs"
        onSubmit={isEditMode ? handleSubmit(update) : handleSubmit(add)}
      >
        <div className="mb-xs flex w-full flex-col gap-y-xs">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState }) => (
              <V2TextField
                {...field}
                assistiveText={fieldState.error?.message}
                id="name"
                isInvalid={Boolean(fieldState.error)}
                label="Name"
                ref={field.ref}
                isRequired
              />
            )}
          />
          <Controller
            control={control}
            name="description"
            render={({ field, fieldState }) => (
              <V2TextArea
                {...field}
                assistiveText={fieldState.error?.message}
                id="description"
                isInvalid={Boolean(fieldState.error)}
                label="Description (optional)"
                ref={field.ref}
                autoResize
              />
            )}
          />
          <Controller
            control={control}
            name="autoUpdate"
            render={({ field }) => (
              <div className="flex w-full flex-col gap-y-xs pt-xs">
                <p
                  className="text-body-small text-text-secondary"
                  id="auto-update-help"
                >
                  Dynamic Policy Collections bump versions of the policies to
                  latest as the repository is scanned.
                </p>
                <V2Checkbox
                  aria-describedby="auto-update-help"
                  id="autoUpdate"
                  isSelected={Boolean(field.value)}
                  isDisabled
                >
                  Auto update
                </V2Checkbox>
              </div>
            )}
          />
        </div>
        <V2Divider />
        <V2Heading className="mb-xs mt-md" level={3}>
          Configuration
        </V2Heading>

        <div className="mb-xs flex w-full flex-col gap-y-xs">
          <Controller
            control={control}
            name="repositoryView.branchName"
            render={({ field, fieldState }) => (
              <V2TextField
                {...field}
                assistiveText="Uses the specified branch name when scanning for policies rather than the repository default (optional)"
                id="branch-name"
                isInvalid={fieldState.invalid}
                label="Branch name"
                ref={field.ref}
              />
            )}
          />
          <Controller
            control={control}
            name="repositoryView.policyDirectories"
            render={({ field, fieldState }) => (
              <V2TextField
                assistiveText="Comma separated list of directories to match from the root (optional)"
                id="policy-directories"
                isInvalid={fieldState.invalid}
                label="Policy directories"
                onBlur={field.onBlur}
                onChange={(value) =>
                  field.onChange(
                    value ? value.split(",").map((dir) => dir.trim()) : [],
                  )
                }
                ref={field.ref}
                value={Array.isArray(field.value) ? field.value.join(", ") : ""}
              />
            )}
          />
          <Controller
            control={control}
            name="repositoryView.policyFileSuffix"
            render={({ field, fieldState }) => (
              <V2TextField
                assistiveText="Comma separated list of policy suffix overrides (optional)"
                id="policy-file-suffix"
                isInvalid={fieldState.invalid}
                label="Policy file suffixes"
                onBlur={field.onBlur}
                onChange={(value) =>
                  field.onChange(
                    value ? value.split(",").map((dir) => dir.trim()) : [],
                  )
                }
                ref={field.ref}
                value={Array.isArray(field.value) ? field.value.join(", ") : ""}
              />
            )}
          />
        </div>

        {showAccessControl && dataQuery ? (
          <div className="mt-lg">
            <V2Heading className="mb-xs" level={3}>
              Access Control
            </V2Heading>

            <ACLFormFields
              aclRef={data}
              availableRoles={POLICY_COLLECTION_ROLES}
              currentRoleChanges={roleChanges}
              disabled={!canEditAcl}
              isEditMode={isEditMode}
              onRoleChanges={handleRoleChanges}
              queryRef={dataQuery}
              target="policy-collection"
            />
          </div>
        ) : null}

        {showDeployAndRun ? (
          <>
            <V2Heading className="mb-sm mt-md" level={3}>
              Deploy and Run (optional)
            </V2Heading>
            <p className="mb-md text-body-medium text-legacy-neutrals-stormyGray">
              Deploy and Run these Policies against one or more Account Groups.
              Additional settings can be modified from the Bindings page.
            </p>
            <div className="px-md">
              <V2Heading className="mb-xs" level={4}>
                Deploy
              </V2Heading>

              <AccountGroupPicker
                fieldName="accountGroups"
                queryRef={dataQuery}
                multiple
                requireDeployPoliciesPermission
              />
              <p className="text-body-small text-text-secondary">
                Select one or more Account Group(s) to deploy to. A separate
                Binding will be created for each group
              </p>

              <V2Heading className="mb-xs mt-md" level={4}>
                Run
              </V2Heading>

              <div className="flex w-full items-center gap-xs">
                <p className="mt-5 shrink-0 text-body-medium text-text-primary">
                  Repeat every
                </p>
                <Controller
                  control={control}
                  name="period"
                  render={({ field }) => {
                    return (
                      <V2NumberField
                        className="w-[7rem]"
                        {...field}
                        assistiveText={errors.period?.message}
                        isDisabled={!hasAccountGroups}
                        isInvalid={Boolean(errors.period)}
                        label="Period"
                        minValue={1}
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="unit"
                  render={({ field }) => (
                    <V2SingleSelectComboBox
                      className="w-[10rem]"
                      isDisabled={!hasAccountGroups}
                      label="Unit"
                      onSelectionChange={field.onChange}
                      placeholder="Select unit…"
                      selectedKey={field.value}
                      triggerClassName="h-[38px] rounded-1"
                      withSearch={false}
                    >
                      <V2ComboBoxItem id="hour">
                        {period ? pluralize("Hour", period) : "Hour"}
                      </V2ComboBoxItem>
                      <V2ComboBoxItem id="day">
                        {period ? pluralize("Day", period) : "Day"}
                      </V2ComboBoxItem>
                    </V2SingleSelectComboBox>
                  )}
                />
                <div className="flex flex-1 items-center gap-xs">
                  <p className="mt-5 shrink-0 whitespace-nowrap text-body-medium text-text-primary">
                    Policy actions
                  </p>
                  <Controller
                    control={control}
                    name="policyActions"
                    render={({ field }) => (
                      <V2SingleSelectComboBox
                        className="flex-1"
                        isDisabled={!hasAccountGroups}
                        label="Policy Actions"
                        onSelectionChange={field.onChange}
                        placeholder="Select policy actions…"
                        selectedKey={field.value}
                        triggerClassName="h-[38px] rounded-1"
                        withSearch={false}
                      >
                        <V2ComboBoxItem id="disabled">Disabled</V2ComboBoxItem>
                        <V2ComboBoxItem id="enabled">Enabled</V2ComboBoxItem>
                      </V2SingleSelectComboBox>
                    )}
                  />
                </div>
              </div>
              <div className="mt-md w-full">
                <V2Alert severity="info">
                  <V2AlertTitle
                    className="text-label-extra-small-strong uppercase text-legacy-navy-L40"
                    severity="info"
                  >
                    RECOMMENDATION
                  </V2AlertTitle>
                  Disable policy actions during the first run of collection.
                  (Dry Run)
                </V2Alert>
              </div>

              <V2Heading className="mb-xs mt-xl" level={4}>
                Source (Advanced)
              </V2Heading>

              <div className="flex w-full flex-wrap items-center gap-xs">
                <p className="mt-5 shrink-0 text-body-medium text-text-primary">
                  Execute policies using
                </p>
                <Controller
                  control={control}
                  name="executeAssetDb"
                  render={({ field }) => (
                    <V2SingleSelectComboBox
                      className="w-[12rem]"
                      isDisabled={!hasAccountGroups}
                      label="Execution Source"
                      onSelectionChange={field.onChange}
                      placeholder="Select execution source…"
                      selectedKey={field.value}
                      triggerClassName="h-[38px] rounded-1"
                      withSearch={false}
                    >
                      <V2ComboBoxItem id="enabled">
                        AssetDB (Faster)
                      </V2ComboBoxItem>
                      <V2ComboBoxItem id="disabled">
                        Cloud APIs (Slower)
                      </V2ComboBoxItem>
                    </V2SingleSelectComboBox>
                  )}
                />
              </div>

              <div className="mt-md w-full">
                <V2Alert severity="info">
                  <V2AlertTitle
                    className="text-label-extra-small-strong uppercase text-legacy-navy-L40"
                    severity="info"
                  >
                    RECOMMENDATION
                  </V2AlertTitle>
                  AssetDB enables faster policy execution and lower API call
                  volume.
                </V2Alert>
              </div>
            </div>
          </>
        ) : null}

        <ErrorAlerts className="mt-md" messages={formErrors} />
        <div className="mb-sm mt-xl flex justify-between">
          <V2Button onPress={onCancel} type="button" variant="secondary">
            Cancel
          </V2Button>
          <V2Button
            aria-label="submit dynamic policy collection form"
            isDisabled={!isValid || isInFlight}
            isPending={isInFlight}
            pendingText={
              isEditMode ? EDIT_FORM_LOADING_SUBMIT_BUTTON_NAME : "Creating…"
            }
            type="submit"
            variant="primary"
          >
            {isEditMode ? EDIT_FORM_SUBMIT_BUTTON_NAME : "Create"}
          </V2Button>
        </div>
      </form>
    </FormProvider>
  );
}
